/**
 * The console's half of a person's confirmation of an operator action — plan-014 F3d, the hand-off from F3a (design
 * §27.9.6; FR12-OPS-01, FR12-OPS-05). Over `inspectOperatorActionConfirmation` and `confirmOperatorAction`
 * (`apps/api/src/routes/operator-action-confirmation/`).
 *
 * OPTIONAL ON THE GATEWAY, like the support hand-off (`../impersonation/impersonation-handoff-gateway.ts`): the methods
 * are merged onto `ConsoleGateway` here and the API binding below is spread into `apiGateway`. The sandbox has no WeTalk
 * operator to prepare a change, so it carries neither, and the page says plainly that there is nothing to confirm here.
 */
import { ApiError, assertOk, type WeTalkApiClient } from "@wetalk/api-client";

/** What the link asks the person to confirm. */
export interface OperatorActionConfirmationView {
  readonly accountName: string;
  readonly actionCode: string;
  /** What changes, in plain words (the API's). */
  readonly description: string;
  readonly expiresAt: string;
  /** A second WeTalk operator approves after the person confirms. */
  readonly secondApproverRequired: boolean;
}

/** The confirmation's outcome. */
export interface OperatorActionConfirmedView {
  readonly state: "awaiting_approval" | "approved";
  readonly description: string;
  /** Until when a second operator may approve it; null once approved. */
  readonly expiresAt: string | null;
}

declare module "../../api/gateway.js" {
  interface ConsoleGateway {
    /** Reads what a confirmation link asks for. `404 not_found` for a used, expired or unknown link. */
    readonly inspectOperatorActionConfirmation?: (token: string) => Promise<OperatorActionConfirmationView>;
    /** Confirms it, once. */
    readonly confirmOperatorAction?: (token: string) => Promise<OperatorActionConfirmedView>;
  }
}

function recordOf(value: unknown, what: string): Readonly<Record<string, unknown>> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new ApiError(500, { error: { code: "internal_error", message: `${what} is not an object` } }, null);
  }
  return value as Readonly<Record<string, unknown>>;
}

function textOf(record: Readonly<Record<string, unknown>>, field: string): string {
  const value = record[field];
  if (typeof value !== "string") throw new ApiError(500, { error: { code: "internal_error", message: `${field} is not text` } }, null);
  return value;
}

function dataOf(answered: Parameters<typeof assertOk>[0], what: string): Readonly<Record<string, unknown>> {
  return recordOf(recordOf(assertOk(answered), what)["data"], `${what}.data`);
}

/** The API binding, spread into `apiGateway` beside the other slices. */
export function operatorActionConfirmationApiSlice(client: WeTalkApiClient): {
  readonly inspectOperatorActionConfirmation: (token: string) => Promise<OperatorActionConfirmationView>;
  readonly confirmOperatorAction: (token: string) => Promise<OperatorActionConfirmedView>;
} {
  return {
    async inspectOperatorActionConfirmation(token) {
      const data = dataOf(await client.inspectOperatorActionConfirmation({ body: { token } }), "inspectOperatorActionConfirmation");
      const secondApprover = data["second_approver_required"];
      return {
        accountName: textOf(data, "account_name"),
        actionCode: textOf(data, "action_code"),
        description: textOf(data, "description"),
        expiresAt: textOf(data, "expires_at"),
        secondApproverRequired: secondApprover === true,
      };
    },
    async confirmOperatorAction(token) {
      const data = dataOf(await client.confirmOperatorAction({ body: { token } }), "confirmOperatorAction");
      const state = textOf(data, "state");
      if (state !== "awaiting_approval" && state !== "approved") {
        throw new ApiError(500, { error: { code: "internal_error", message: `state ${state} is not a confirmation outcome` } }, null);
      }
      const expiresAt = data["expires_at"];
      return { state, description: textOf(data, "description"), expiresAt: typeof expiresAt === "string" ? expiresAt : null };
    },
  };
}
